import { CircularProgress, Stack, useTheme } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { PublicApiAllowedOperation, PublicApiPermissionItem } from '@provision/entity-types';
import * as React from 'react';
import { usePublicApiPermissions } from 'src/graphql';

const ROOT = 'all';

interface TreeNode {
  name: string;
  children: TreeNode[];
  level: number;
}

/**
 * Turns the flat `PublicApiPermissionItem[]` returned by the backend into
 * the nested tree the table renders. The DAG is rooted at `ROOT` (`all`).
 */
function buildTree(
  items: PublicApiPermissionItem[],
  rootNode: string = ROOT,
  level = 1,
): TreeNode {
  const byResource = new Map(items.map((i) => [i.resource, i] as const));
  const buildNode = (name: string, depth: number): TreeNode => ({
    name,
    level: depth,
    children: (byResource.get(name)?.children ?? []).map((child) =>
      buildNode(child, depth + 1),
    ),
  });
  return buildNode(rootNode, level);
}

type ActionState = Record<string, boolean>;

type ActionOption = {
  label: string;
  value: string;
};

const ACTION_OPTIONS: ActionOption[] = [
  { label: 'Read', value: 'read' },
  { label: 'Write', value: 'write' },
];

/** Stable empty `ancestorOperations` passed to the root row. */
const EMPTY_ANCESTOR_OPERATIONS: Readonly<Record<string, boolean>> = Object.freeze({});

const PermissionTableRow = (props: {
  state: Record<string, ActionState>;
  ancestorOperations: Readonly<Record<string, boolean>>;
  item: TreeNode;
  actions: ActionOption[];
  onToggle: (name: string, op: string, next: boolean) => void;
}) => {
  const { item, actions, onToggle, state, ancestorOperations } = props;
  const theme = useTheme();

  const myState = state[item.name] ?? {};

  // Children inherit "covered" status from us if either an ancestor already
  // had the op selected OR we ourselves have it selected. This is how the
  // implicit-coverage signal flows down the tree. It's a 2-entry object so we
  // don't bother memoising — the row itself is cheap to re-render.
  const childAncestorOperations: Record<string, boolean> = Object.fromEntries(
    actions.map(({ value: op }) => [op, ancestorOperations[op] || myState[op]]),
  );

  return (
    <>
      <TableRow key={item.name}>
        <TableCell
          component="th"
          scope="row"
          sx={{ paddingLeft: theme.spacing(item.level * 2) }}
        >
          {item.name}
        </TableCell>
        {actions.map(({ value }) => {
          const coveredByAncestor = ancestorOperations[value];
          const checked = !coveredByAncestor && myState[value];
          return (
            <TableCell key={value} align="center">
              <Checkbox
                color="primary"
                value={value}
                checked={checked}
                indeterminate={coveredByAncestor}
                disabled={coveredByAncestor}
                onChange={(_event: React.ChangeEvent<HTMLInputElement>, nextValue: boolean) =>
                  onToggle(item.name, value, nextValue)
                }
              />
            </TableCell>
          );
        })}
      </TableRow>
      {item.children.map((childItem) => (
        <PermissionTableRow
          key={childItem.name}
          actions={actions}
          item={childItem}
          state={state}
          ancestorOperations={childAncestorOperations}
          onToggle={onToggle}
        />
      ))}
    </>
  );
};

/**
 * Folds the structured grant list emitted by the GraphQL boundary into the
 * `{ resource: { operation: true } }` shape used internally by the table.
 */
function allowedOperationsToState(
  allowedOperations: PublicApiAllowedOperation[],
): Record<string, ActionState> {
  const next: Record<string, ActionState> = {};
  for (const { resource, operations } of allowedOperations) {
    next[resource] = Object.fromEntries(operations.map((op) => [op, true]));
  }
  return next;
}

function stateToAllowedOperations(
  state: Record<string, ActionState>,
): PublicApiAllowedOperation[] {
  return Object.entries(state).flatMap(([resource, opsState]) => {
    const operations = Object.entries(opsState)
      .filter(([, selected]) => selected)
      .map(([operation]) => operation);
    return operations.length === 0 ? [] : [{ resource, operations }];
  });
}

/** Walks every descendant of `node` (children, grandchildren, …) into `acc`. */
function collectDescendantNames(node: TreeNode, acc: string[] = []): string[] {
  for (const child of node.children) {
    acc.push(child.name);
    collectDescendantNames(child, acc);
  }
  return acc;
}

export const PermissionTable = (props: {
  onChange: (value: PublicApiAllowedOperation[]) => void;
  /** Pre-selects rows in edit mode. Treated as the initial value only. */
  initialOperations?: PublicApiAllowedOperation[];
}) => {
  const { permissions, isLoading } = usePublicApiPermissions();
  const [state, setState] = React.useState<Record<string, ActionState>>(() =>
    allowedOperationsToState(props.initialOperations ?? []),
  );
  const { onChange } = props;

  const tree = React.useMemo(
    () => (permissions && permissions.length > 0 ? buildTree(permissions) : null),
    [permissions],
  );

  // Index of node-by-name so toggling a row can quickly walk its descendants.
  const nodeByName = React.useMemo(() => {
    const acc = new Map<string, TreeNode>();
    const visit = (n: TreeNode) => {
      acc.set(n.name, n);
      n.children.forEach(visit);
    };
    if (tree) visit(tree);
    return acc;
  }, [tree]);

  const onToggle = React.useCallback(
    (name: string, op: string, next: boolean) => {
      setState((prev) => {
        const nextState: Record<string, ActionState> = { ...prev };

        const rowState: ActionState = { ...(nextState[name] ?? {}) };
        if (next) {
          rowState[op] = true;
        } else {
          delete rowState[op];
        }
        if (Object.keys(rowState).length === 0) {
          delete nextState[name];
        } else {
          nextState[name] = rowState;
        }

        // Selecting an op on a row makes that op implicit on every descendant,
        // so drop redundant descendant entries to keep ActionState in sync
        // with what the user sees (descendants render as indeterminate, not
        // selected).
        if (next) {
          const subtreeRoot = nodeByName.get(name);
          if (subtreeRoot) {
            for (const descendant of collectDescendantNames(subtreeRoot)) {
              const descState = nextState[descendant];
              if (descState?.[op]) {
                const copy = { ...descState };
                delete copy[op];
                if (Object.keys(copy).length === 0) {
                  delete nextState[descendant];
                } else {
                  nextState[descendant] = copy;
                }
              }
            }
          }
        }

        onChange(stateToAllowedOperations(nextState));
        return nextState;
      });
    },
    [nodeByName, onChange],
  );

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Resource</TableCell>
            {ACTION_OPTIONS.map(({ label, value }) => (
              <TableCell key={value} align="center">
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tree ? (
            <PermissionTableRow
              item={tree}
              actions={ACTION_OPTIONS}
              state={state}
              ancestorOperations={EMPTY_ANCESTOR_OPERATIONS}
              onToggle={onToggle}
            />
          ) : (
            <TableRow>
              <TableCell colSpan={ACTION_OPTIONS.length + 1} align="center">
                {isLoading ? (
                  <Stack alignItems="center" py={2}>
                    <CircularProgress size={20} />
                  </Stack>
                ) : (
                  'No permissions available.'
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
